import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CloudRain, Wind, Waves, Sun, CloudSnow, AlertTriangle, TrendingUp, Navigation, Cloud } from 'lucide-react';

const WeatherForecastPage = () => {
  const [layers, setLayers] = useState({
    depth: true,
    temperature: true,
    salinity: false,
  });

  const [vesselData] = useState({
    lat: 21.3,  // Hawaii coordinates for NOAA API
    lon: -157.8,
    speed: 9.8,
    heading: 212,
  });

  const [forecast, setForecast] = useState([]);
  const [isRealData, setIsRealData] = useState(false);
  const [dataSource, setDataSource] = useState('Loading...');

  const toggleLayer = (layer) => {
    setLayers(prev => ({
      ...prev,
      [layer]: !prev[layer],
    }));
  };

  // Fetch real weather forecast
  useEffect(() => {
    const fetchForecast = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/weather/forecast?lat=${vesselData.lat}&lon=${vesselData.lon}`);
        const result = await response.json();
        
        if (result.success && result.data && result.data.forecast) {
          setIsRealData(true);
          setDataSource(result.source);
          
          // Map NOAA forecast to our format
          const mappedForecast = result.data.forecast.map(period => ({
            day: period.day,
            date: new Date(period.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            icon: getWeatherIcon(period.shortForecast),
            temp: `${period.temperature}°${period.temperatureUnit}`,
            wind: period.windSpeed,
            waves: estimateWaves(period.windSpeed),
            conditions: period.shortForecast,
            severity: calculateSeverity(period.windSpeed),
            isDaytime: period.isDaytime
          }));
          
          setForecast(mappedForecast);
        } else {
          // Fallback to simulated data
          setDataSource('Simulated Data');
          setForecast(getSimulatedForecast());
        }
      } catch (error) {
        console.error('Error fetching weather forecast:', error);
        setDataSource('Simulated Data (API Error)');
        setForecast(getSimulatedForecast());
      }
    };

    fetchForecast();
  }, [vesselData.lat, vesselData.lon]);

  // Helper: Get icon based on forecast text
  const getWeatherIcon = (forecast) => {
    const text = forecast.toLowerCase();
    if (text.includes('rain') || text.includes('shower')) return CloudRain;
    if (text.includes('storm') || text.includes('thunder')) return CloudSnow;
    if (text.includes('wind') || text.includes('breezy')) return Wind;
    if (text.includes('cloud') || text.includes('overcast')) return Cloud;
    if (text.includes('sunny') || text.includes('clear')) return Sun;
    return Cloud;
  };

  // Helper: Estimate wave height from wind speed
  const estimateWaves = (windSpeed) => {
    const speed = parseInt(windSpeed);
    if (speed < 15) return '1.5 m';
    if (speed < 20) return '2.0 m';
    if (speed < 25) return '2.5 m';
    if (speed < 30) return '3.0 m';
    return '4.0 m';
  };

  // Helper: Calculate severity from wind speed
  const calculateSeverity = (windSpeed) => {
    const speed = parseInt(windSpeed);
    if (speed > 25) return 'high';
    if (speed > 15) return 'medium';
    return 'low';
  };

  // Simulated forecast fallback
  const getSimulatedForecast = () => [
    { day: 'Today', date: 'Jan 7', icon: Sun, temp: '22°C', wind: '15 kt', waves: '1.8 m', conditions: 'Clear', severity: 'low' },
    { day: 'Tomorrow', date: 'Jan 8', icon: Sun, temp: '23°C', wind: '12 kt', waves: '1.5 m', conditions: 'Sunny', severity: 'low' },
    { day: 'Wed', date: 'Jan 9', icon: CloudRain, temp: '21°C', wind: '18 kt', waves: '2.3 m', conditions: 'Light Rain', severity: 'medium' },
    { day: 'Thu', date: 'Jan 10', icon: CloudRain, temp: '20°C', wind: '22 kt', waves: '2.8 m', conditions: 'Rain', severity: 'medium' },
    { day: 'Fri', date: 'Jan 11', icon: Wind, temp: '19°C', wind: '28 kt', waves: '3.5 m', conditions: 'Windy', severity: 'high' },
    { day: 'Sat', date: 'Jan 12', icon: CloudSnow, temp: '18°C', wind: '32 kt', waves: '4.2 m', conditions: 'Storm', severity: 'high' },
    { day: 'Sun', date: 'Jan 13', icon: Wind, temp: '20°C', wind: '25 kt', waves: '3.1 m', conditions: 'Clearing', severity: 'medium' },
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-destructive';
      case 'medium': return 'text-amber-400';
      default: return 'text-emerald-400';
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'high': return <Badge variant="destructive">Caution</Badge>;
      case 'medium': return <Badge variant="outline" className="border-amber-400 text-amber-400">Moderate</Badge>;
      default: return <Badge variant="outline" className="border-emerald-400 text-emerald-400">Safe</Badge>;
    }
  };

  return (
    <div 
      className="grid grid-cols-1 lg:grid-cols-[auto_1fr] lg:grid-rows-[56px_1fr] min-h-screen bg-background text-foreground"
      data-testid="weather-forecast-page-container"
    >
      <Sidebar layers={layers} toggleLayer={toggleLayer} currentRoute="/weather" />
      <Topbar vesselData={vesselData} />
      <main className="pt-14 lg:pt-0 lg:col-start-2 lg:row-start-2 p-4 md:p-6 relative z-0">
        <div className="mb-6" data-testid="page-header">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <CloudRain className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">Weather Forecast & Routing</h1>
            </div>
            <Badge variant="outline" className={isRealData ? "border-emerald-400 text-emerald-400" : "border-amber-400 text-amber-400"}>
              {isRealData ? '🌐 Live NOAA Data' : '📊 Simulated'}
            </Badge>
          </div>
          <p className="text-muted-foreground">7-day forecast, storm tracking, and optimal route suggestions • {dataSource}</p>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6" data-testid="weather-forecast-page-grid">
          {/* Current Conditions */}
          <Card className="col-span-12 xl:col-span-8" data-testid="current-conditions-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wind className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Current Conditions</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div data-testid="current-temp">
                  <div className="flex items-center gap-2 mb-2">
                    <Sun className="h-5 w-5 text-amber-400" />
                    <span className="text-sm text-muted-foreground">Temperature</span>
                  </div>
                  <p className="text-3xl font-bold font-mono">22°C</p>
                  <p className="text-xs text-muted-foreground mt-1">Feels like 21°C</p>
                </div>
                <div data-testid="current-wind">
                  <div className="flex items-center gap-2 mb-2">
                    <Wind className="h-5 w-5 text-sky-400" />
                    <span className="text-sm text-muted-foreground">Wind Speed</span>
                  </div>
                  <p className="text-3xl font-bold font-mono">15 kt</p>
                  <p className="text-xs text-muted-foreground mt-1">SW Direction</p>
                </div>
                <div data-testid="current-waves">
                  <div className="flex items-center gap-2 mb-2">
                    <Waves className="h-5 w-5 text-cyan-400" />
                    <span className="text-sm text-muted-foreground">Wave Height</span>
                  </div>
                  <p className="text-3xl font-bold font-mono">1.8 m</p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    Rising
                  </p>
                </div>
                <div data-testid="current-pressure">
                  <div className="flex items-center gap-2 mb-2">
                    <Sun className="h-5 w-5 text-primary" />
                    <span className="text-sm text-muted-foreground">Pressure</span>
                  </div>
                  <p className="text-3xl font-bold font-mono">1012</p>
                  <p className="text-xs text-muted-foreground mt-1">hPa</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weather Alerts */}
          <Card className="col-span-12 xl:col-span-4" data-testid="weather-alerts-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Alerts</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="alert-storm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-sm text-amber-400">Storm Warning</h4>
                    <p className="text-xs text-muted-foreground mt-1">Expected Fri-Sat: 28-32 kt winds, 3.5-4.2m waves</p>
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-testid="alert-clear">
                <div className="flex items-start gap-2">
                  <Sun className="h-4 w-4 text-emerald-400 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-sm text-emerald-400">Favorable Conditions</h4>
                    <p className="text-xs text-muted-foreground mt-1">Next 48 hours suitable for research operations</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 7-Day Forecast */}
          <Card className="col-span-12" data-testid="forecast-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CloudRain className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">7-Day Forecast</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
                {forecast.map((day, idx) => {
                  const Icon = day.icon;
                  return (
                    <div 
                      key={idx}
                      className="p-4 rounded-lg bg-secondary/20 border border-border/40 text-center"
                      data-testid={`forecast-day-${idx}`}
                    >
                      <h4 className="font-medium mb-1" data-testid={`forecast-day-name-${idx}`}>{day.day}</h4>
                      <p className="text-xs text-muted-foreground mb-3">{day.date}</p>
                      <Icon className={`h-8 w-8 mx-auto mb-3 ${getSeverityColor(day.severity)}`} />
                      <p className="text-2xl font-bold font-mono mb-2">{day.temp}</p>
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <div className="flex items-center justify-center gap-1">
                          <Wind className="h-3 w-3" />
                          <span>{day.wind}</span>
                        </div>
                        <div className="flex items-center justify-center gap-1">
                          <Waves className="h-3 w-3" />
                          <span>{day.waves}</span>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">{day.conditions}</p>
                      <div className="mt-3 flex justify-center">
                        {getSeverityBadge(day.severity)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Weather Routing Recommendations */}
          <Card className="col-span-12 xl:col-span-6" data-testid="routing-recommendations-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Navigation className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Routing Recommendations</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-testid="route-optimal">
                <h4 className="font-medium text-emerald-400 mb-2 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-400"></span>
                  Optimal Route
                </h4>
                <p className="text-sm text-muted-foreground mb-3">Current course (212°) is optimal for next 48 hours</p>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ETA at waypoint</span>
                    <span className="font-mono">Jan 9, 14:30</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Distance remaining</span>
                    <span className="font-mono">247 nm</span>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="route-alternative">
                <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-amber-400"></span>
                  Alternative Route Suggested
                </h4>
                <p className="text-sm text-muted-foreground mb-3">Consider course change to 195° on Jan 10 to avoid storm</p>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Additional distance</span>
                    <span className="font-mono">+32 nm</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time savings</span>
                    <span className="font-mono text-emerald-400">-4.5 hours</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Marine Forecast Details */}
          <Card className="col-span-12 xl:col-span-6" data-testid="marine-forecast-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Waves className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Marine Forecast Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-3">Sea State</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-border/30">
                      <span className="text-muted-foreground">Significant Wave Height</span>
                      <span className="font-mono">1.8 m</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border/30">
                      <span className="text-muted-foreground">Wave Period</span>
                      <span className="font-mono">8.2 sec</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border/30">
                      <span className="text-muted-foreground">Wave Direction</span>
                      <span className="font-mono">SW</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">Swell Height</span>
                      <span className="font-mono">1.2 m</span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-border/40">
                  <h4 className="text-sm font-medium mb-3">Visibility & Conditions</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-border/30">
                      <span className="text-muted-foreground">Visibility</span>
                      <span className="font-mono">12+ nm</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border/30">
                      <span className="text-muted-foreground">Precipitation</span>
                      <span className="font-mono">0 mm/hr</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">Cloud Cover</span>
                      <span className="font-mono">25%</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default WeatherForecastPage;
